import React from 'react';

/**
 * Resend delivery-status pill.
 *
 * Reads the `delivery_status` written by routes/resendWebhook.js
 * (email.delivered / email.bounced / email.complained /
 * email.delivery_delayed). Renders nothing when status is null —
 * the surrounding "Sent <ts>" line already conveys the at-send-time
 * state, and showing an empty pill would be more confusing than
 * informative. `bounceReason` surfaces on hover as a tooltip.
 *
 * Color palette per the user's spec:
 *   delivered  → Doorly green (#acf44e / #1a1a1a)
 *   bounced    → Doorly red   (#ff082f / #fff)
 *   complained → Doorly red   (treated like bounced — recipient
 *                              marked spam, equally bad)
 *   delayed    → amber        (#f59e0b / #1a1a1a) — Resend hasn't
 *                              given up yet; will retry
 */
export default function EmailDeliveryPill({ status, bounceReason, lastEventAt, style }) {
  if (!status) return null;
  const cfg = ({
    delivered:  { label: 'Delivered',          bg: '#acf44e', color: '#1a1a1a' },
    bounced:    { label: 'Bounced',            bg: '#ff082f', color: '#ffffff' },
    complained: { label: 'Marked as Spam',     bg: '#ff082f', color: '#ffffff' },
    delayed:    { label: 'Delivery Delayed',   bg: '#f59e0b', color: '#1a1a1a' },
  })[status] || { label: status, bg: '#e5e7eb', color: '#374151' };

  const titleParts = [];
  if (bounceReason) titleParts.push(bounceReason);
  if (lastEventAt) {
    try {
      titleParts.push(`Updated ${new Date(lastEventAt).toLocaleString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
      })}`);
    } catch (_) { /* noop — bad date string */ }
  }
  const title = titleParts.join(' · ') || undefined;

  return (
    <span title={title}
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: 9999,
        fontSize: 10,
        fontWeight: 800,
        background: cfg.bg,
        color: cfg.color,
        textTransform: 'uppercase',
        letterSpacing: 0.3,
        whiteSpace: 'nowrap',
        ...(style || {}),
      }}>
      {cfg.label}
    </span>
  );
}
